/**
 * Serviço para gerenciamento de Metas
 * Centraliza a lógica de criação, atualização e exclusão de metas
 * garantindo sincronização com transações e relatórios
 */

import { differenceInCalendarDays } from 'date-fns';
import { Goal, Transaction, TransactionStatus, useFinanceStore } from '../store/useFinanceStore';
import { toDateInputValue } from '../utils/date';
import { supabase } from '../lib/supabase';

export interface GoalServiceResult {
  success: boolean;
  message: string;
  error?: any;
}

type CategoryResolution = {
  id: string | null;
  name: Transaction['category'];
};

const CATEGORY_IDS = {
  FINANCE_MAIN: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa',
  OTHERS_MAIN: 'bbbbbbbb-bbbb-bbbb-bbbb-bbbbbbbbbbbb',
  INCOME_MAIN: '44444444-4444-4444-4444-444444444444',
  GOAL_CONTRIBUTION: '55555555-5555-5555-5555-555555555555',
} as const;

const CONFIRMED_STATUSES: TransactionStatus[] = ['confirmed', 'paid', 'active'];
const FORECAST_STATUSES: TransactionStatus[] = ['scheduled', 'overdue'];
const CYCLE_LOOKBACK_MONTHS = 3;

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const normalizeTransactionStatus = (
  transaction: Transaction,
  referenceDate: Date
): TransactionStatus => {
  let status = transaction.status ?? 'active';

  if (status === 'active') {
    status = 'confirmed';
  }

  if (status === 'scheduled') {
    const txDate = new Date(transaction.date);
    if (!Number.isNaN(txDate.getTime()) && txDate < referenceDate) {
      status = 'overdue';
    }
  }

  return status;
};

interface GoalContributionSafetyResult {
  allowed: boolean;
  message?: string;
  metrics: {
    confirmedBalance: number;
    forecastBalance: number;
    directProjection: number;
    conservativeProjection: number;
    historicalGuard: number;
    averageDailyOutflow: number;
    remainingDays: number;
    lookbackMonths: number;
    observedMonths: number;
  };
}

const evaluateGoalContributionSafety = (amount: number): GoalContributionSafetyResult => {
  const { transactions } = useFinanceStore.getState();
  const absoluteAmount = Math.abs(amount);

  if (absoluteAmount === 0) {
    return {
      allowed: true,
      metrics: {
        confirmedBalance: 0,
        forecastBalance: 0,
        directProjection: 0,
        conservativeProjection: 0,
        historicalGuard: 0,
        averageDailyOutflow: 0,
        remainingDays: 0,
        lookbackMonths: CYCLE_LOOKBACK_MONTHS,
        observedMonths: 0,
      },
    };
  }

  const today = new Date();
  const referenceDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const cycleStart = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 1);
  const cycleEnd = new Date(referenceDate.getFullYear(), referenceDate.getMonth() + 1, 0);
  const cycleStartTime = cycleStart.getTime();
  const cycleEndTime = cycleEnd.getTime();

  let confirmedBalance = 0;
  let forecastBalance = 0;
  let forecastExpenses = 0;

  transactions.forEach((transaction) => {
    if (transaction.status === 'cancelled') {
      return;
    }

    const txDate = new Date(transaction.date);
    if (Number.isNaN(txDate.getTime())) {
      return;
    }

    const txTime = txDate.getTime();
    if (txTime < cycleStartTime || txTime > cycleEndTime) {
      return;
    }

    const status = normalizeTransactionStatus(transaction, referenceDate);
    const amountValue = transaction.amount;
    const absAmount = Math.abs(amountValue);

    if (CONFIRMED_STATUSES.includes(status)) {
      confirmedBalance += amountValue;
    } else if (FORECAST_STATUSES.includes(status)) {
      forecastBalance += amountValue;
      if (amountValue < 0) {
        forecastExpenses += absAmount;
      }
    }
  });

  const directProjection = confirmedBalance + forecastBalance - absoluteAmount;
  const remainingDays = Math.max(0, differenceInCalendarDays(cycleEnd, referenceDate));
  const cycleEndLabel = cycleEnd.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
  });

  if (directProjection < 0) {
    return {
      allowed: false,
      message: `Aporte bloqueado: compromissos confirmados e agendados até ${cycleEndLabel} já consumiriam ${formatCurrency(Math.abs(directProjection))} além do saldo disponível.`,
      metrics: {
        confirmedBalance,
        forecastBalance,
        directProjection,
        conservativeProjection: directProjection,
        historicalGuard: 0,
        averageDailyOutflow: 0,
        remainingDays,
        lookbackMonths: CYCLE_LOOKBACK_MONTHS,
        observedMonths: 0,
      },
    };
  }

  let historicalOutflowTotal = 0;
  let historicalDaysTotal = 0;
  let observedMonths = 0;

  for (let offset = 1; offset <= CYCLE_LOOKBACK_MONTHS; offset += 1) {
    const historyStart = new Date(referenceDate.getFullYear(), referenceDate.getMonth() - offset, 1);
    const historyEnd = new Date(referenceDate.getFullYear(), referenceDate.getMonth() - offset + 1, 0);
    const historyStartTime = historyStart.getTime();
    const historyEndTime = historyEnd.getTime();

    let monthlyOutflow = 0;

    transactions.forEach((transaction) => {
      if (transaction.amount >= 0 || transaction.status === 'cancelled') {
        return;
      }

      const txDate = new Date(transaction.date);
      if (Number.isNaN(txDate.getTime())) {
        return;
      }

      const txTime = txDate.getTime();
      if (txTime < historyStartTime || txTime > historyEndTime) {
        return;
      }

      const status = normalizeTransactionStatus(transaction, historyEnd);
      if (!CONFIRMED_STATUSES.includes(status)) {
        return;
      }

      monthlyOutflow += Math.abs(transaction.amount);
    });

    if (monthlyOutflow > 0) {
      historicalOutflowTotal += monthlyOutflow;
      historicalDaysTotal += historyEnd.getDate();
      observedMonths += 1;
    }
  }

  const averageDailyOutflow =
    historicalDaysTotal > 0 ? historicalOutflowTotal / historicalDaysTotal : 0;
  const expectedHistoricalOutflow = averageDailyOutflow * remainingDays;
  const historicalGuard = Math.max(0, expectedHistoricalOutflow - forecastExpenses);

  const conservativeProjection = directProjection - historicalGuard;

  if (conservativeProjection < 0) {
    const shortfall = Math.abs(conservativeProjection);

    const message =
      observedMonths > 0
        ? `Aporte bloqueado: mantendo a média de gastos dos últimos ${observedMonths} ciclo${observedMonths > 1 ? 's' : ''} (${formatCurrency(averageDailyOutflow)}/dia), ainda precisamos reservar ${formatCurrency(historicalGuard)} para os ${remainingDays} dia${remainingDays === 1 ? '' : 's'} restantes. O saldo final ficaria negativo em ${formatCurrency(shortfall)}.`
        : `Aporte bloqueado: não há histórico suficiente para liberar esse aporte sem comprometer despesas previstas até ${cycleEndLabel}.`;

    return {
      allowed: false,
      message,
      metrics: {
        confirmedBalance,
        forecastBalance,
        directProjection,
        conservativeProjection,
        historicalGuard,
        averageDailyOutflow,
        remainingDays,
        lookbackMonths: CYCLE_LOOKBACK_MONTHS,
        observedMonths,
      },
    };
  }

  return {
    allowed: true,
    metrics: {
      confirmedBalance,
      forecastBalance,
      directProjection,
      conservativeProjection,
      historicalGuard,
      averageDailyOutflow,
      remainingDays,
      lookbackMonths: CYCLE_LOOKBACK_MONTHS,
      observedMonths,
    },
  };
};

function resolveGoalDepositCategory(): CategoryResolution {
  const { categories } = useFinanceStore.getState();
  const goalContribution = categories.find(
    (category) =>
      category.id === CATEGORY_IDS.GOAL_CONTRIBUTION || category.name === 'Aporte da Meta'
  );

  if (goalContribution) {
    return { id: goalContribution.id, name: 'Aporte da Meta' };
  }
  const savingsCandidate = categories.find((category) =>
    category.parent_id === CATEGORY_IDS.FINANCE_MAIN && ['Poupança', 'Investimentos'].includes(category.name)
  );

  if (savingsCandidate) {
    return { id: savingsCandidate.id, name: 'Financeiro' };
  }

  const financeMain = categories.find((category) => category.id === CATEGORY_IDS.FINANCE_MAIN || category.name === 'Financeiro');
  if (financeMain) {
    return { id: financeMain.id, name: 'Financeiro' };
  }

  return {
    id: null,
    name: 'Aporte da Meta',
  };
}

function resolveGoalRefundCategory(): CategoryResolution {
  const { categories } = useFinanceStore.getState();
  const goalContribution = categories.find(
    (category) =>
      category.id === CATEGORY_IDS.GOAL_CONTRIBUTION || category.name === 'Aporte da Meta'
  );

  if (goalContribution) {
    return { id: goalContribution.id, name: 'Aporte da Meta' };
  }
  const incomeMain = categories.find((category) => category.id === CATEGORY_IDS.INCOME_MAIN || category.name === 'Entrada principal');
  return {
    id: incomeMain ? incomeMain.id : null,
    name: goalContribution ? 'Aporte da Meta' : 'Entrada principal',
  };
}

/**
 * Registra uma transação ao adicionar valor a uma meta
 */
export const registerGoalTransaction = async (
  goalName: string,
  amount: number,
  addTransaction: (transaction: Omit<Transaction, 'id'>) => Promise<void>
): Promise<void> => {
  if (amount <= 0) return;

  const depositCategory = resolveGoalDepositCategory();
  const transaction = {
    description: `Depósito para meta: ${goalName}`,
    amount: -Math.abs(amount),
    category: depositCategory.name,
    type: 'expense' as const,
    date: toDateInputValue(),
    status: 'confirmed' as const,
    category_id: depositCategory.id,
    notes: `Valor reservado para a meta "${goalName}"`,
    recurrenceType: 'none' as const,
    recurrenceCount: null,
    recurrenceEndDate: null,
  };
  
  await addTransaction(transaction);
};

/**
 * Registra reembolso ao cancelar uma meta
 */
export const registerGoalRefund = async (
  goalName: string,
  amount: number,
  addTransaction: (transaction: Omit<Transaction, 'id'>) => Promise<void>
): Promise<void> => {
  if (amount <= 0) return;

  const incomeCategory = resolveGoalRefundCategory();
  await addTransaction({
    description: `Reembolso de Meta: ${goalName}`,
    amount: Math.abs(amount),
    category: incomeCategory.name,
    type: 'income',
    date: toDateInputValue(),
    status: 'confirmed',
    category_id: incomeCategory.id,
    notes: `Reembolso do valor reservado originalmente para a meta "${goalName}"`,
    recurrenceType: 'none',
    recurrenceCount: null,
    recurrenceEndDate: null,
  });
};

/**
 * Cria uma nova meta e sempre registra transação informativa
 */
export const createGoalWithTransaction = async (
  goalData: Omit<Goal, 'id'>,
  addGoal: (goal: Omit<Goal, 'id'>) => Promise<void>,
  addTransaction: (transaction: Omit<Transaction, 'id'>) => Promise<void>,
  calculateGoalAnalytics?: (goalId: string) => Promise<void>
): Promise<GoalServiceResult> => {
  try {
    if (goalData.currentAmount > 0) {
      const safetyCheck = evaluateGoalContributionSafety(goalData.currentAmount);
      if (!safetyCheck.allowed) {
        return {
          success: false,
          message:
            safetyCheck.message ||
            'Saldo projetado insuficiente para reservar esse valor na meta neste momento.'
        };
      }
    }

    // Cria a meta primeiro
    await addGoal(goalData);

    // SEMPRE cria transação informativa sobre a criação da meta
    await addTransaction({
      description: `Meta criada: ${goalData.name} com valor alvo R$ ${goalData.targetAmount.toFixed(2)}`,
      amount: 0, // Transação informativa, não afeta saldo
      category: 'Outros',
      type: 'expense',
      date: toDateInputValue(),
      status: 'active'
    });

    // Se houver valor guardado inicial, registra como despesa
    if (goalData.currentAmount > 0) {
      await registerGoalTransaction(
        goalData.name,
        goalData.currentAmount,
        addTransaction
      );
    }

    // Recalcular analytics após criar meta
    // NOTA: goalId não está disponível aqui porque addGoal não retorna o ID
    // Analytics será calculado quando a meta for carregada do Supabase

    return {
      success: true,
      message: 'Meta criada com sucesso!'
    };
  } catch (error) {
    console.error('Erro ao criar meta:', error);
    return {
      success: false,
      message: 'Erro ao criar meta',
      error
    };
  }
};

/**
 * Atualiza uma meta e registra transação apenas para diferença de valor
 * AGORA COM VALIDAÇÃO DE SALDO quando aumentar currentAmount
 */
export const updateGoalWithTransaction = async (
  goalId: string,
  currentGoal: Goal,
  updatedData: Partial<Goal>,
  updateGoal: (id: string, goal: Partial<Goal>) => Promise<void>,
  addTransaction: (transaction: Omit<Transaction, 'id'>) => Promise<void>,
  calculateGoalAnalytics?: (goalId: string) => Promise<void>
): Promise<GoalServiceResult> => {
  try {
    // Calcula a diferença de valor se currentAmount foi alterado
    const previousAmount = currentGoal.currentAmount;
    const newAmount = updatedData.currentAmount ?? previousAmount;
    const difference = newAmount - previousAmount;

    // Se houve aumento no valor, VALIDAR SALDO usando contribute_to_goal
    if (difference > 0) {
      const safetyCheck = evaluateGoalContributionSafety(difference);
      if (!safetyCheck.allowed) {
        return {
          success: false,
          message:
            safetyCheck.message ||
            'Aporte bloqueado pelos compromissos previstos para este ciclo.'
        };
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return {
          success: false,
          message: 'Usuário não autenticado'
        };
      }

      // Usar função SQL com validação
      const { data, error } = await supabase.rpc('contribute_to_goal', {
        p_user_id: user.id,
        p_goal_id: goalId,
        p_amount: difference,
        p_description: `Atualização manual: ${updatedData.name || currentGoal.name}`
      });

      if (error || !data || !data.success) {
        const errorMsg = data?.error || 'Erro ao validar saldo';
        const availableBalance = data?.available_balance || 0;
        const missingAmount = data?.missing_amount || 0;
        
        return {
          success: false,
          message: `${errorMsg}. Saldo disponível: R$ ${availableBalance.toFixed(2)}. Faltam: R$ ${missingAmount.toFixed(2)}`
        };
      }

      // Sucesso! A função SQL já atualizou tudo
      // Só precisamos atualizar outros campos (nome, deadline, etc)
      const fieldsToUpdate = { ...updatedData };
      delete fieldsToUpdate.currentAmount; // Já foi atualizado pela função SQL
      
      if (Object.keys(fieldsToUpdate).length > 0) {
        await updateGoal(goalId, fieldsToUpdate);
      }
    }
    // Se houve redução no valor, registra como reembolso
    else if (difference < 0) {
      await registerGoalRefund(
        updatedData.name || currentGoal.name,
        Math.abs(difference),
        addTransaction
      );
      await updateGoal(goalId, updatedData);
    }
    // Se não alterou currentAmount, apenas atualiza outros campos
    else {
      await updateGoal(goalId, updatedData);
    }

    // Recalcular analytics após atualizar
    if (calculateGoalAnalytics) {
      await calculateGoalAnalytics(goalId);
    }

    return {
      success: true,
      message: 'Meta atualizada com sucesso!'
    };
  } catch (error) {
    console.error('Erro ao atualizar meta:', error);
    return {
      success: false,
      message: 'Erro ao atualizar meta',
      error
    };
  }
};

/**
 * Cancela uma meta e marca as transações relacionadas como canceladas
 * Permite rastreamento do histórico mantendo relatórios limpos
 * Remove também eventos da agenda relacionados à meta
 */
export const cancelGoalWithRefund = async (
  goal: Goal,
  cancellationReason: string,
  deleteGoal: (id: string) => Promise<void>,
  addTransaction: (transaction: Omit<Transaction, 'id'>) => Promise<void>,
  transactions: Transaction[],
  updateTransaction: (id: string, transaction: Partial<Transaction>) => Promise<void>,
  events: any[],
  deleteEvent: (id: string) => Promise<void>
): Promise<GoalServiceResult> => {
  try {
    // Encontra todas as transações relacionadas a esta meta (exceto já canceladas)
    const goalTransactions = transactions.filter(t => 
      t.description.includes(goal.name) && 
      t.status !== 'cancelled' &&
      (t.description.includes('Depósito para meta') || 
       t.description.includes('Depósito adicional para meta') ||
       t.description.includes('Meta criada'))
    );

    // Marca todas as transações como canceladas (não deleta)
    for (const transaction of goalTransactions) {
      await updateTransaction(transaction.id, {
        status: 'cancelled',
        cancellationReason: cancellationReason,
        description: transaction.description.startsWith('[CANCELADA]') 
          ? transaction.description 
          : `[CANCELADA] ${transaction.description}`
      });
    }

    // Cria transação de reembolso (receita) apenas se houver valor guardado
    if (goal.currentAmount > 0) {
      const refundCategory = resolveGoalRefundCategory();
      await addTransaction({
        description: `Reembolso de Meta Cancelada: ${goal.name}`,
        amount: goal.currentAmount,
        category: refundCategory.name,
        type: 'income',
        date: toDateInputValue(),
        status: 'active',
        category_id: refundCategory.id,
        notes: `Valor retornado após cancelamento da meta "${goal.name}"`
      });
    }

    // Remove eventos da agenda relacionados a esta meta
    const goalEvents = events.filter(e => 
      e.type === 'goal' && 
      (e.title?.includes(goal.name) || e.description?.includes(goal.name))
    );
    
    for (const event of goalEvents) {
      await deleteEvent(event.id);
    }

    // Remove a meta
    await deleteGoal(goal.id);

    return {
      success: true,
      message: 'Meta cancelada com sucesso!'
    };
  } catch (error) {
    console.error('Erro ao cancelar meta:', error);
    return {
      success: false,
      message: 'Erro ao cancelar meta',
      error
    };
  }
};

/**
 * Adiciona valor rápido a uma meta (para botões quick-add)
 * AGORA COM VALIDAÇÃO DE SALDO via SQL function
 */
export const quickAddToGoal = async (
  goal: Goal,
  amount: number,
  updateGoal: (id: string, goal: Partial<Goal>) => Promise<void>,
  addTransaction: (transaction: Omit<Transaction, 'id'>) => Promise<void>,
  calculateGoalAnalytics?: (goalId: string) => Promise<void>
): Promise<GoalServiceResult> => {
  try {
    const safetyCheck = evaluateGoalContributionSafety(amount);
    if (!safetyCheck.allowed) {
      return {
        success: false,
        message:
          safetyCheck.message ||
          'Aporte bloqueado: ajuste lançamentos futuros antes de reservar esse valor.'
      };
    }

    // Buscar user_id atual
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return {
        success: false,
        message: 'Usuário não autenticado'
      };
    }

    // Usar função SQL contribute_to_goal com VALIDAÇÃO de saldo
    const { data, error } = await supabase.rpc('contribute_to_goal', {
      p_user_id: user.id,
      p_goal_id: goal.id,
      p_amount: amount,
      p_description: null // usa descrição padrão
    });

    if (error) {
      console.error('Erro ao contribuir para meta:', error);
      return {
        success: false,
        message: 'Erro ao processar contribuição',
        error
      };
    }

    // Verificar resposta da função
    if (!data || !data.success) {
      const errorMsg = data?.error || 'Erro desconhecido';
      const availableBalance = data?.available_balance || 0;
      const missingAmount = data?.missing_amount || 0;
      
      return {
        success: false,
        message: `${errorMsg}. Saldo disponível: R$ ${availableBalance.toFixed(2)}. Faltam: R$ ${missingAmount.toFixed(2)}`
      };
    }

    // Sucesso! Recarregar dados
    if (calculateGoalAnalytics) {
      await calculateGoalAnalytics(goal.id);
    }

    const newBalance = data.remaining_balance || 0;
    return {
      success: true,
      message: `R$ ${amount.toFixed(2)} adicionado à meta! Saldo restante: R$ ${newBalance.toFixed(2)}`
    };
  } catch (error) {
    console.error('Erro ao adicionar valor à meta:', error);
    return {
      success: false,
      message: 'Erro ao adicionar valor à meta',
      error
    };
  }
};
